// @ts-nocheck
/**
 * Admin Customer Detail Page
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Logger, LogCategory } from '@/utils/logger';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { useAdminStore } from '@/stores/adminStore';
import { ROUTES } from '@/constants';
import type { Company } from '@/types';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';

export function AdminCustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();

  const {
    fetchCompanyById,
    updateCompany,
    addCredits,
    changePlan,
    fetchNotes,
    addNote,
  } = useAdminStore();

  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [creditsForm, setCreditsForm] = useState({ amount: '', reason: '' });
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [noteContent, setNoteContent] = useState('');
  const [notes, setNotes] = useState<any[]>([]);

  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        navigate(ROUTES.ADMIN_CUSTOMERS);
        return;
      }

      setIsLoading(true);
      try {
        const data = await fetchCompanyById(id);
        if (data) {
          setCompany(data);
          setSelectedPlan(data.plan);
        } else {
          showError('Error', 'Customer not found');
          navigate(ROUTES.ADMIN_CUSTOMERS);
        }
      } catch (error) {
        Logger.error(LogCategory.DATA, 'Failed to load customer', 'AdminCustomerDetailPage', undefined, error);
        showError('Error', 'Failed to load customer data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleAddCredits = async () => {
    if (!company || !creditsForm.amount) return;
    setIsSaving(true);
    try {
      await addCredits(company.id, parseInt(creditsForm.amount), creditsForm.reason);
      setCompany({ ...company, credits: company.credits + parseInt(creditsForm.amount) });
      setShowCreditsModal(false);
      setCreditsForm({ amount: '', reason: '' });
      showSuccess('Success', 'Credits added');
    } catch (error) {
      showError('Error', 'Failed to add credits');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePlan = async () => {
    if (!company) return;
    setIsSaving(true);
    try {
      await changePlan(company.id, selectedPlan);
      setCompany({ ...company, plan: selectedPlan });
      setShowPlanModal(false);
      showSuccess('Success', `Plan changed to ${selectedPlan}`);
    } catch (error) {
      showError('Error', 'Failed to change plan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!company || !noteContent.trim()) return;
    setIsSaving(true);
    try {
      await addNote(company.id, noteContent.trim(), true);
      setShowNoteModal(false);
      setNoteContent('');
      showSuccess('Success', 'Note added');
    } catch (error) {
      showError('Error', 'Failed to add note');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      </AdminLayout>
    );
  }

  if (!company) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Customer not found</p>
          <Link to={ROUTES.ADMIN_CUSTOMERS}>
            <Button className="mt-4">Back to Customers</Button>
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to={ROUTES.ADMIN_CUSTOMERS}>
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{String(company.name)}</h2>
            <p className="text-slate-500">{String(company.domain)}</p>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm font-medium text-slate-500 mb-2">Plan</p>
              <Badge variant="info" size="lg">
                {String(company.plan).charAt(0).toUpperCase() + String(company.plan).slice(1)}
              </Badge>
              <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => setShowPlanModal(true)}>
                Change Plan
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <p className="text-sm font-medium text-slate-500 mb-2">Credits</p>
              <p className="text-2xl font-bold text-slate-900">{Number(company.credits).toLocaleString()}</p>
              <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => setShowCreditsModal(true)}>
                Add Credits
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <p className="text-sm font-medium text-slate-500 mb-2">Customer Since</p>
              <p className="text-sm text-slate-900">{new Date(company.createdAt).toLocaleDateString()}</p>
              <p className="text-xs text-slate-400 mt-3">ID: {String(company.id)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Company Info */}
        <Card>
          <CardHeader title="Company Information" />
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-slate-500">Name</p>
                <p className="text-slate-900">{String(company.name)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Domain</p>
                <p className="text-slate-900">{String(company.domain)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Industry</p>
                <p className="text-slate-900">{String(company.industry || '—')}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">Billing Email</p>
                <p className="text-slate-900">{String(company.billingEmail || '—')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader
            title="Internal Notes"
            action={
              <Button variant="outline" size="sm" onClick={() => setShowNoteModal(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Note
              </Button>
            }
          />
          <CardContent>
            <p className="text-sm text-slate-500 text-center py-4">Notes will appear here after adding them.</p>
          </CardContent>
        </Card>
      </div>

      {/* Credits Modal */}
      <Modal isOpen={showCreditsModal} onClose={() => setShowCreditsModal(false)} title="Add Credits" size="sm">
        <div className="space-y-4">
          <Input
            label="Amount"
            type="number"
            value={creditsForm.amount}
            onChange={(e) => setCreditsForm({ ...creditsForm, amount: e.target.value })}
            placeholder="e.g., 500"
          />
          <Input
            label="Reason"
            value={creditsForm.reason}
            onChange={(e) => setCreditsForm({ ...creditsForm, reason: e.target.value })}
            placeholder="e.g., Promotional credit"
          />
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowCreditsModal(false)}>Cancel</Button>
            <Button onClick={handleAddCredits} isLoading={isSaving}>Add Credits</Button>
          </div>
        </div>
      </Modal>

      {/* Plan Modal */}
      <Modal isOpen={showPlanModal} onClose={() => setShowPlanModal(false)} title="Change Plan" size="sm">
        <div className="space-y-4">
          <Select
            label="Plan"
            value={selectedPlan}
            onChange={(e) => setSelectedPlan(e.target.value)}
            options={[
              { value: 'trial', label: 'Trial' },
              { value: 'starter', label: 'Starter - $49/mo' },
              { value: 'growth', label: 'Growth - $149/mo' },
              { value: 'pro', label: 'Pro - $349/mo' },
              { value: 'enterprise', label: 'Enterprise - $799/mo' },
            ]}
          />
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowPlanModal(false)}>Cancel</Button>
            <Button onClick={handleChangePlan} isLoading={isSaving}>Change Plan</Button>
          </div>
        </div>
      </Modal>

      {/* Note Modal */}
      <Modal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)} title="Add Note" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
            <textarea
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-y"
              rows={5}
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Enter your note..."
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowNoteModal(false)}>Cancel</Button>
            <Button onClick={handleAddNote} isLoading={isSaving}>Add Note</Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
}
